import { Button } from '@/components/ui/Button';
import { Check, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ActionProposee } from '../types/assistant.types';

/**
 * La carte de confirmation : ce qui va partir, dans les mots du métier.
 * C'est le seul endroit où l'assistant s'arrête pour demander une décision.
 * Une fois décidée, la carte se réduit à une ligne de résultat — elle ne
 * reste pas ouverte à encombrer la conversation.
 */
export function CarteAction({
  action,
  onConfirmer,
  onAnnuler,
}: {
  action: ActionProposee;
  onConfirmer: () => void;
  onAnnuler: () => void;
}) {
  if (action.etat === 'annulee') {
    return (
      <p className="mt-2 flex items-center gap-2 text-sm text-neutral-500">
        <X className="h-4 w-4 shrink-0" />
        Annulé. Rien n’a été fait.
      </p>
    );
  }

  if (action.etat === 'faite' || action.etat === 'echec') {
    const echec = action.etat === 'echec';
    return (
      <p
        className={`mt-2 flex items-center gap-2 text-sm ${
          echec ? 'text-error-700' : 'text-neutral-900'
        }`}
      >
        {echec ? (
          <X className="text-error-600 h-4 w-4 shrink-0" />
        ) : (
          <Check className="text-success-600 h-4 w-4 shrink-0" />
        )}
        <span>{action.resultat?.texte}</span>
        {action.resultat?.lien && (
          <Link
            to={action.resultat.lien}
            className="text-primary-600 hover:text-primary-700 font-medium underline-offset-2 hover:underline"
          >
            Voir
          </Link>
        )}
      </p>
    );
  }

  const enCours = action.etat === 'en_cours';

  return (
    <div className="mt-3 max-w-md rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-neutral-900">{action.titre}</p>
      <dl className="mt-3 space-y-2">
        {action.resume.map(ligne => (
          <div key={ligne.libelle} className="flex gap-3 text-sm">
            <dt className="w-36 shrink-0 text-neutral-500">{ligne.libelle}</dt>
            <dd className="min-w-0 flex-1 font-medium text-neutral-900">
              {ligne.valeur}
            </dd>
          </div>
        ))}
      </dl>
      {/* Une confirmation qui n'a pas pu partir (droits invérifiables à
          l'instant) le dit ici : la proposition reste ouverte, on réessaie. */}
      {!enCours && action.resultat?.texte && (
        <p className="text-warning-700 mt-3 text-sm">{action.resultat.texte}</p>
      )}
      <div className="mt-4 flex items-center gap-2">
        <Button
          variant="brand"
          size="sm"
          onClick={onConfirmer}
          disabled={enCours}
          className="gap-2"
        >
          {enCours ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              En cours
            </>
          ) : (
            'Confirmer'
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onAnnuler}
          disabled={enCours}
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}
